import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MapPin } from './ReceiptIcons';
import { buildStaticMapUrl, validPoint } from '../utils/rideStaticMap';

// Same key the rest of the passenger app uses for Google Maps (services/maps.js).
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.REACT_APP_FIREBASE_API_KEY;
// Keep in step with `.ride-receipt-map` in RideDetails.css.
const MAP_HEIGHT = 184;
const FALLBACK_WIDTH = 360;

const MapPlaceholder = ({ state, t }) => (
  <div className="ride-receipt-map-placeholder" role="status">
    <MapPin size={24} />
    <span>{t(state === 'loading' ? 'details.mapLoading' : 'details.mapUnavailable')}</span>
  </div>
);

function StaticMapImage({ url, width, t }) {
  const [state, setState] = useState('loading');
  useEffect(() => {
    if (state !== 'loading') return undefined;
    const timer = setTimeout(() => setState('unavailable'), 12000);
    return () => clearTimeout(timer);
  }, [state]);

  return <>
    {state !== 'unavailable' && <img
      className="ride-receipt-map-image"
      src={url}
      alt={t('details.routeMap')}
      width={width}
      height={MAP_HEIGHT}
      decoding="async"
      draggable="false"
      onLoad={() => setState('ready')}
      onError={() => setState('unavailable')}
    />}
    {state !== 'ready' && <MapPlaceholder state={state} t={t} />}
  </>;
}

/**
 * Pickup and drop pins on a small map picture. It is a single Google Static Maps
 * image (cheap, cached by the phone, no live map), requested at the width it is
 * actually shown at so nothing is cropped. If the picture cannot load, a quiet
 * placeholder shows instead and the rest of the receipt is unaffected.
 */
export default function RideDetailsMap({ pickup, destination, t }) {
  const frame = useRef(null);
  const [width, setWidth] = useState(null);
  const hasPoints = validPoint(pickup) && validPoint(destination);

  // Reframe the image after rotation or a change in the available screen width.
  useLayoutEffect(() => {
    if (!hasPoints) return undefined;
    const measure = () => setWidth(Math.round(frame.current?.clientWidth || FALLBACK_WIDTH));
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(frame.current);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [hasPoints]);

  const url = hasPoints && width
    ? buildStaticMapUrl({ pickup, destination, key: MAPS_KEY, width, height: MAP_HEIGHT })
    : null;

  if (!hasPoints) return null;

  return (
    <div className="ride-receipt-map" ref={frame}>
      {url ? <StaticMapImage key={url} url={url} width={width} t={t} />
        : <MapPlaceholder state={width ? 'unavailable' : 'loading'} t={t} />}
    </div>
  );
}
